import React from 'react';
import PublicLayout from '../components/public/PublicLayout';
import SEO from '../components/common/SEO';
import { FileText, Mail } from 'lucide-react';

const TermsConditionsPage = () => {
  return (
    <PublicLayout>
      <SEO 
        title="Terms & Conditions - ARCA"
        description="Read the Africa Respiratory Care Association's Terms and Conditions for using our website and services."
        keywords="terms and conditions, ARCA, user agreement, membership terms"
      />
      
      {/* Hero Section */}
      <section className="position-relative text-white py-5" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)' }}>
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <FileText size={60} className="mb-3" />
              <h1 className="display-4 fw-bold mb-3">Terms & Conditions</h1>
              <p className="lead mb-0">
                Please read these terms carefully before using our services
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Terms & Conditions Content */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="card shadow-sm border-0">
                <div className="card-body p-4 p-lg-5">
                  
                  {/* Header Information */}
                  <div className="mb-4 pb-4 border-bottom">
                    <h2 className="h4 fw-bold mb-3" style={{ color: '#6B8E23' }}>Africa Respiratory Care Association (ARCA)</h2>
                    <p className="mb-2"><strong>Last Updated:</strong> May 20, 2025</p>
                    <p className="mb-0">
                      <Mail size={16} className="me-2" style={{ color: '#6B8E23' }} />
                      <a href="mailto:info@africaresp.org" style={{ color: '#6B8E23', textDecoration: 'none' }}>
                        info@africaresp.org
                      </a>
                    </p>
                  </div>

                  {/* 1. Acceptance of Terms */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>1. Acceptance of Terms</h3>
                    <p className="text-muted">
                      By accessing and using the Africa Respiratory Care Association (ARCA) website and services, you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these Terms and Conditions, please do not use our website or services.
                    </p>
                  </div>

                  {/* 2. Use of Website */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>2. Use of Website</h3>
                    <p className="text-muted mb-2">You agree to use our website only for lawful purposes and in a way that does not infringe the rights of others or restrict their use and enjoyment of the website. Prohibited behavior includes:</p>
                    <ul className="text-muted">
                      <li className="mb-2">Harassing or causing distress or inconvenience to any person</li>
                      <li className="mb-2">Transmitting obscene or offensive content</li>
                      <li className="mb-2">Disrupting the normal flow of dialogue within our website</li>
                      <li className="mb-2">Attempting to gain unauthorized access to any portion of the website</li>
                    </ul>
                  </div>

                  {/* 3. Membership Terms */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>3. Membership Terms</h3>
                    <ul className="text-muted">
                      <li className="mb-2">Membership is subject to approval by ARCA</li>
                      <li className="mb-2">Members must provide accurate and complete information during registration</li>
                      <li className="mb-2">Membership fees are non-refundable unless otherwise stated</li>
                      <li className="mb-2">ARCA reserves the right to terminate membership for violation of these terms</li>
                      <li className="mb-2">Members are responsible for maintaining the confidentiality of their account credentials</li>
                    </ul>
                  </div>

                  {/* 4. Intellectual Property */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>4. Intellectual Property</h3>
                    <p className="text-muted">
                      All content on this website, including but not limited to text, graphics, logos, images, and software, is the property of ARCA or its content suppliers and is protected by international copyright laws. Unauthorized use of any materials may violate copyright, trademark, and other laws.
                    </p>
                  </div>

                  {/* 5. User Content */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>5. User Content</h3>
                    <p className="text-muted">
                      By submitting content to our website (including but not limited to forum posts, comments, and testimonials), you grant ARCA a non-exclusive, royalty-free, perpetual, and worldwide license to use, reproduce, modify, and distribute such content. You represent and warrant that you own or have the necessary rights to submit such content.
                    </p>
                  </div>

                  {/* 6. Events and Registrations */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>6. Events and Registrations</h3>
                    <ul className="text-muted">
                      <li className="mb-2">Event registrations are subject to availability and ARCA's approval</li>
                      <li className="mb-2">Cancellation policies vary by event and will be clearly stated during registration</li>
                      <li className="mb-2">ARCA reserves the right to cancel or reschedule events and will provide appropriate notice</li>
                      <li className="mb-2">Participants are expected to conduct themselves professionally at all ARCA events</li>
                    </ul>
                  </div>

                  {/* 7. Payment Terms */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>7. Payment Terms</h3>
                    <ul className="text-muted">
                      <li className="mb-2">All payments must be made in the currency specified on the website</li>
                      <li className="mb-2">Payment information is processed securely through third-party payment processors</li>
                      <li className="mb-2">Refund policies are outlined for specific services and will be honored accordingly</li>
                      <li className="mb-2">ARCA reserves the right to change fees and charges with appropriate notice</li>
                    </ul>
                  </div>

                  {/* 8. Disclaimer of Warranties */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>8. Disclaimer of Warranties</h3>
                    <p className="text-muted">
                      This website and its content are provided "as is" without any warranties, express or implied. ARCA does not warrant that the website will be uninterrupted or error-free, that defects will be corrected, or that the website or the server that makes it available are free of viruses or other harmful components.
                    </p>
                  </div>

                  {/* 9. Limitation of Liability */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>9. Limitation of Liability</h3>
                    <p className="text-muted">
                      To the fullest extent permitted by law, ARCA shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the website or services, even if ARCA has been advised of the possibility of such damages.
                    </p>
                  </div>

                  {/* 10. Links to Third-Party Websites */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>10. Links to Third-Party Websites</h3>
                    <p className="text-muted">
                      Our website may contain links to third-party websites. These links are provided for your convenience only. ARCA has no control over the content of these sites and accepts no responsibility for them or for any loss or damage that may arise from your use of them.
                    </p>
                  </div>

                  {/* 11. Privacy */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>11. Privacy</h3>
                    <p className="text-muted">
                      Your use of our website is also governed by our Privacy Policy. Please review our{' '}
                      <a href="/privacy-policy" style={{ color: '#6B8E23', textDecoration: 'none' }}>
                        Privacy Policy
                      </a>
                      {' '}to understand our practices regarding your personal data.
                    </p>
                  </div>

                  {/* 12. Modifications to Terms */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>12. Modifications to Terms</h3>
                    <p className="text-muted">
                      ARCA reserves the right to modify these Terms and Conditions at any time. Changes will be posted on this page with an updated revision date. Your continued use of the website following any changes constitutes acceptance of those changes.
                    </p>
                  </div>

                  {/* 13. Governing Law */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>13. Governing Law</h3>
                    <p className="text-muted">
                      These Terms and Conditions shall be governed by and construed in accordance with applicable laws. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the appropriate courts.
                    </p>
                  </div>

                  {/* 14. Contact Information */}
                  <div className="mb-0">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>14. Contact Information</h3>
                    <p className="text-muted mb-2">
                      If you have any questions about these Terms and Conditions, please contact us:
                    </p>
                    <div className="p-3 bg-light rounded">
                      <p className="mb-1 fw-bold" style={{ color: '#2C5234' }}>Africa Respiratory Care Association (ARCA)</p>
                      <p className="mb-0">
                        <Mail size={16} className="me-2" style={{ color: '#6B8E23' }} />
                        <a href="mailto:info@africaresp.org" style={{ color: '#6B8E23', textDecoration: 'none' }}>
                          info@africaresp.org
                        </a>
                      </p>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default TermsConditionsPage;
